import { countryDocumentationApi } from "../../../lib/api/handlers/countryDocumentation";
import { zimbabweLocationsApi } from "../../../lib/api/handlers/zimbabweLocations";
import { fetchFacilityDirectory } from "../../../config/facilities";
import {
  environmentalReferencesApi,
  type EnvironmentalReferenceType,
} from "../../../lib/api/handlers/environmentalReferences";
import {
  conveyanceReferencesApi,
  type ConveyanceReferenceType,
} from "../../../lib/api/handlers/conveyanceReferences";
import {
  cargoReferencesApi,
  type CargoReferenceType,
} from "../../../lib/api/handlers/cargoReferences";
import {
  hazardousReferencesApi,
  type HazardousReferenceType,
} from "../../../lib/api/handlers/hazardousReferences";
import {
  humanRemainsReferencesApi,
  type HumanRemainsReferenceType,
} from "../../../lib/api/handlers/humanRemainsReferences";
import { documentRequirementsApi } from "../../../lib/api/handlers/documentRequirements";
import {
  resolvePolicyTtl,
  type OfflineCachePolicy,
  type OfflineCachePolicyKey,
} from "../../../lib/offline/cachePolicy";
import { metaApi } from "../../../lib/api/handlers/meta";
import { outbreakReferencesApi } from "../../../lib/api/handlers/outbreakReferences";

export type OfflineConfigResource<T = unknown> = {
  key: string;
  fetch: () => Promise<T>;
  ttlMs?: number;
  policyKey?: OfflineCachePolicyKey;
  warmup?: boolean;
  handlesPersistence?: boolean;
};

const HOUR_MS = 1000 * 60 * 60;
const HALF_DAY_TTL = 12 * HOUR_MS;
const ONE_DAY_TTL = 24 * HOUR_MS;
const REFERENCE_FETCH_LIMIT = 500;

type ReferenceGroupConfig<T extends string> = {
  prefix: string;
  types: readonly T[];
  ttlMs?: number;
  policyKey?: OfflineCachePolicyKey;
  requester: (type: T) => Promise<unknown>;
};

const createReferenceResources = <T extends string>({
  prefix,
  types,
  ttlMs = ONE_DAY_TTL,
  policyKey = "referenceTtlMs",
  requester,
}: ReferenceGroupConfig<T>): OfflineConfigResource[] =>
  types.map((type) => ({
    key: `${prefix}/${type}`,
    ttlMs,
    policyKey,
    warmup: true,
    fetch: () => requester(type),
  }));

// Primary Screening specific reference data
export const PRIMARY_SCREENING_CONFIG: OfflineConfigResource[] = [
  {
    key: "country-documentation",
    ttlMs: HALF_DAY_TTL,
    policyKey: "documentTtlMs",
    fetch: () => countryDocumentationApi.list(0, 500),
  },
];

const LOCATION_RESOURCES: OfflineConfigResource[] = [
  {
    key: "locations/zimbabwe",
    ttlMs: ONE_DAY_TTL,
    policyKey: "locationTtlMs",
    fetch: () => zimbabweLocationsApi.list(),
  },
];

const FACILITY_RESOURCES: OfflineConfigResource[] = [
  {
    key: "facilities/directory",
    ttlMs: ONE_DAY_TTL,
    policyKey: "facilityTtlMs",
    fetch: () => fetchFacilityDirectory(true),
  },
];

const DOCUMENT_REQUIREMENT_RESOURCES: OfflineConfigResource[] = [
  {
    key: "document-requirements",
    ttlMs: HALF_DAY_TTL,
    policyKey: "documentTtlMs",
    fetch: () => documentRequirementsApi.listActive(),
  },
];

const ENVIRONMENTAL_REFERENCE_TYPES: readonly EnvironmentalReferenceType[] = [
  "business-types",
  "business-centres",
  "food-locations",
  "food-premises",
  "vector-types",
  "trap-types",
  "vector-species",
  "site-types",
  "equipment-traps",
] as const;

const ENVIRONMENTAL_REFERENCE_CONFIG = createReferenceResources({
  prefix: "environmental",
  types: ENVIRONMENTAL_REFERENCE_TYPES,
  ttlMs: ONE_DAY_TTL,
  requester: (type) => environmentalReferencesApi.search(type, { limit: REFERENCE_FETCH_LIMIT }),
});

const CONVEYANCE_REFERENCE_TYPES: readonly ConveyanceReferenceType[] = [
  "airlines",
  "airline-representatives",
  "flight-attendants",
  "chemicals",
  "disinfection-products",
  "fumigation-providers",
  "transport-companies",
  "transport-drivers",
  "transport-vehicles",
  "transport-vehicle-types",
  "vessel-types",
  "fumigation-methods",
] as const;

const CONVEYANCE_REFERENCE_CONFIG = createReferenceResources({
  prefix: "conveyance",
  types: CONVEYANCE_REFERENCE_TYPES,
  ttlMs: ONE_DAY_TTL,
  requester: (type) => conveyanceReferencesApi.search(type, { limit: REFERENCE_FETCH_LIMIT }),
});

const CARGO_REFERENCE_TYPES: readonly CargoReferenceType[] = [
  "suppliers",
  "receivers",
  "manufacturers",
  "brands",
  "products",
  "laboratories",
  "fumigation-providers",
] as const;

const CARGO_REFERENCE_CONFIG = createReferenceResources({
  prefix: "cargo",
  types: CARGO_REFERENCE_TYPES,
  ttlMs: ONE_DAY_TTL,
  requester: (type) => cargoReferencesApi.search(type, { limit: REFERENCE_FETCH_LIMIT }),
});

const HAZARDOUS_REFERENCE_TYPES: readonly HazardousReferenceType[] = [
  "substances",
  "hazard-codes",
  "hazard-classes",
  "hazard-groups",
  "importers",
  "exporters",
] as const;

const HAZARDOUS_REFERENCE_CONFIG = createReferenceResources({
  prefix: "hazardous",
  types: HAZARDOUS_REFERENCE_TYPES,
  ttlMs: ONE_DAY_TTL,
  requester: (type) => hazardousReferencesApi.search(type, { limit: REFERENCE_FETCH_LIMIT }),
});

const HUMAN_REMAINS_REFERENCE_TYPES: readonly HumanRemainsReferenceType[] = ["document-types"] as const;

const HUMAN_REMAINS_REFERENCE_CONFIG = createReferenceResources({
  prefix: "human-remains",
  types: HUMAN_REMAINS_REFERENCE_TYPES,
  ttlMs: ONE_DAY_TTL,
  requester: (type) => humanRemainsReferencesApi.search(type, { limit: REFERENCE_FETCH_LIMIT }),
});

const OUTBREAK_REFERENCE_CONFIG = createReferenceResources({
  prefix: "outbreak",
  types: ["drill-types"],
  ttlMs: ONE_DAY_TTL,
  requester: (type) => outbreakReferencesApi.search(type, { limit: REFERENCE_FETCH_LIMIT }),
});

const META_RESOURCES: OfflineConfigResource[] = [
  {
    key: "meta/declaration",
    policyKey: "metaTtlMs",
    warmup: true,
    handlesPersistence: true,
    fetch: () => metaApi.declaration(),
  },
];

export const OFFLINE_CONFIG_RESOURCES: OfflineConfigResource[] = [
  ...PRIMARY_SCREENING_CONFIG,
  ...LOCATION_RESOURCES,
  ...FACILITY_RESOURCES,
  ...DOCUMENT_REQUIREMENT_RESOURCES,
  ...ENVIRONMENTAL_REFERENCE_CONFIG,
  ...CONVEYANCE_REFERENCE_CONFIG,
  ...CARGO_REFERENCE_CONFIG,
  ...HAZARDOUS_REFERENCE_CONFIG,
  ...HUMAN_REMAINS_REFERENCE_CONFIG,
  ...OUTBREAK_REFERENCE_CONFIG,
  ...META_RESOURCES,
];

export const resolveResourceTtl = (
  resource: OfflineConfigResource,
  policy: OfflineCachePolicy,
): number | null => {
  const candidateTtl = resource.policyKey ? resolvePolicyTtl(policy, resource.policyKey) : resource.ttlMs;
  const fallback = resolvePolicyTtl(policy);
  const ttl = typeof candidateTtl === "number" && candidateTtl > 0 ? candidateTtl : fallback;
  return ttl > 0 ? ttl : null;
};
